import { utcToZonedTime, format, formatInTimeZone } from 'date-fns-tz';
import DATE_FORMATS from '@constants/dateFormat';
import { StaticRange } from 'react-date-range';
import {
  isAfter,
  isEqual,
  isToday,
  subDays,
  addDays,
  endOfDay,
  addMonths,
  endOfWeek,
  isSameDay,
  startOfDay,
  endOfMonth,
  startOfWeek,
  startOfMonth,
  endOfTomorrow,
  startOfTomorrow,
  differenceInMinutes,
  differenceInDays,
  isValid,
} from 'date-fns';
import StaticCustomDateRange from '@th-types/static-date-range.type';
import {
  AvailabilityType,
  AvailabilityTypeLabels,
  IAvailabilityData,
} from '@company/types/workerAvailability.type';
import defaultAvatar from '@assets/default-avatar.png';

const UTC_TIMEZONE = 'UTC';

export const preDefinedDates = {
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfToday: startOfDay(new Date()),
  endOfToday: endOfDay(new Date()),
  startOfYesterday: startOfDay(addDays(new Date(), -1)),
  endOfYesterday: endOfDay(addDays(new Date(), -1)),
  startOfTomorrow: startOfTomorrow(),
  endOfTomorrow: endOfTomorrow(),
  startOfNextWeek: startOfDay(addDays(endOfWeek(new Date()), 1)),
  endOfNextWeek: startOfDay(addDays(endOfWeek(new Date()), 7)),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfNextMonth: startOfMonth(addMonths(new Date(), 1)),
  endOfNextMonth: endOfMonth(addMonths(new Date(), 1)),
};

// used for ThDateRange
export const createStaticRanges = (
  ranges: StaticCustomDateRange[]
): StaticRange[] => {
  return ranges.map((range: any) => {
    return {
      range: range.range,
      isSelected: (rangeVal: any) => {
        const rangeDates = range.range();
        return (
          rangeDates &&
          rangeVal &&
          rangeVal.startDate &&
          rangeVal.endDate &&
          isSameDay(rangeVal.startDate, rangeDates.startDate) &&
          isSameDay(rangeVal.endDate, rangeDates.endDate)
        );
      },
      label: range.label,
    } as StaticRange;
  });
};

export const futureStaticRanges = createStaticRanges([
  {
    label: 'Today',
    range: () => ({
      startDate: preDefinedDates.startOfToday,
      endDate: preDefinedDates.endOfToday,
    }),
  },
  {
    label: 'Tomorrow',
    range: () => ({
      startDate: preDefinedDates.startOfTomorrow,
      endDate: preDefinedDates.endOfTomorrow,
    }),
  },
  {
    label: 'This week',
    range: () => ({
      startDate: preDefinedDates.startOfWeek,
      endDate: preDefinedDates.endOfWeek,
    }),
  },
  {
    label: 'Next week',
    range: () => ({
      startDate: preDefinedDates.startOfNextWeek,
      endDate: preDefinedDates.endOfNextWeek,
    }),
  },
  {
    label: 'This Month',
    range: () => ({
      startDate: preDefinedDates.startOfMonth,
      endDate: preDefinedDates.endOfMonth,
    }),
  },
  {
    label: 'Next Month',
    range: () => ({
      startDate: preDefinedDates.startOfNextMonth,
      endDate: preDefinedDates.endOfNextMonth,
    }),
  },
]);

const formatLocalDate = (
  date: Date | string,
  dateFormat: string = DATE_FORMATS.SHORT_DATE_FORMAT,
  timeZone?: string
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!isValid(dateObj)) {
    return '';
  }
  return timeZone
    ? formatInTimeZone(dateObj, timeZone, dateFormat)
    : format(dateObj, dateFormat);
};

const formatOpenDateRange = (
  start: Date,
  end?: Date | null,
  dateFormat: string = DATE_FORMATS.DATE_FORMAT_SHORT,
  excludeEnd: boolean = false
): string => {
  try {
    // Ensure we have Date objects
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : end && new Date(end);
    const startString = format(startDate, dateFormat);
    return excludeEnd
      ? startString
      : `${startString} - ${
          endDate ? format(new Date(endDate), dateFormat) : 'Present'
        }`;
  } catch (e) {
    console.warn(e);
    return '';
  }
};

const formatTimeOrDateNow = (dateString: string, dateFormat?: string) => {
  const date = new Date(dateString);
  return isToday(date)
    ? format(date, DATE_FORMATS.TIME_FORMAT)
    : format(date, dateFormat || DATE_FORMATS.DATE_FORMAT);
};
/**
 * date type should be string only, but sometimes it's a Date object
 * because in some types we are uisnng Date instead of srting. The Date
 * we receive from the API is a Date string and not a Date object.
 */
const convertToTimezone = (date: string | Date, tzString: string) => {
  const utcDate = typeof date === 'string' ? new Date(date) : date;
  return utcToZonedTime(utcDate, tzString);
};

const parseToDate = (dateString: string, timezoneId: string) =>
  new Date(
    new Date(dateString).toLocaleString('en-US', { timeZone: timezoneId })
  );

const getDateNowByTimezone = (timezone?: string) => {
  return new Date(
    new Date().toLocaleString('en-US', { timeZone: timezone || UTC_TIMEZONE })
  );
};

const getTodayDateByTimezone = (timezone: string): Date => {
  const utcStr = new Date(new Date().toUTCString());
  const zonedDate = utcToZonedTime(utcStr, timezone);
  const offset = zonedDate.getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset > 0 ? '-' : '+';
  const timezoneOffset = `${sign}${String(hours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')}`;

  const formattedDate = format(zonedDate, 'yyyy-MM-dd', { timeZone: timezone });
  return new Date(`${formattedDate}T00:00:00${timezoneOffset}`);
};

const getDifferenceInMinutes = (date1: Date, date2: Date): number => {
  return differenceInMinutes(date1, date2);
};

const dateDaysAgo = (days: number): Date => {
  return subDays(new Date(), days);
};

const calculateRemainingTime = (time: string) => {
  const lastSurveyDate = new Date(time).valueOf();
  const now = new Date().valueOf();

  const diffInMs = now - lastSurveyDate;

  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 1) {
    return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${
      minutes !== 1 ? 's' : ''
    }`;
  }

  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

const isSameDateOrAfter = (date1: Date, date2: Date): boolean => {
  return isAfter(date1, date2) || isEqual(date1, date2);
};

const isDateAfterOrToday = (date: string, timezone: string) => {
  const today = getDateNowByTimezone(timezone);
  const dateValue = parseToDate(date, timezone);
  isToday(dateValue);
  today.setHours(0, 0, 0, 0);
  return isAfter(dateValue, today) || isEqual(dateValue, today);
};

const isDateAfterOrNow = (date: string, timezone: string) => {
  const dateNow = getDateNowByTimezone(timezone);
  const dateValue = parseToDate(date, timezone);
  return isAfter(dateValue, dateNow) || isEqual(dateValue, dateNow);
};

const createDateTimeFromDifferentDates = (date: Date, time: Date) => {
  return new Date(
    `${format(date, DATE_FORMATS.DATE_FORMAT)} ${format(
      time,
      DATE_FORMATS.TIME_FORMAT
    )}`
  );
};

const formatWorkDateRange = (
  startDate: Date,
  endDate: Date,
  dateFormat: string
) => {
  return `${format(startDate, dateFormat)} - ${format(endDate, dateFormat)}`;
};

const safeFormat = (date: Date | undefined, fmt: string) => {
  return date && isValid(date) ? format(date, fmt) : '';
};

/**
 * Convert 24h time string to 12h format
 * @param time - Time string in 24h format (e.g., "14:30")
 * @returns Time string in 12h format (e.g., "2:30 PM")
 */
const formatTime12h = (time: string): string => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Chunks multi-day availabilities into single-day events for calendar display
 */
const rangeOfDateToChunkDays = (
  workerAvailabilities: IAvailabilityData[]
): IAvailabilityData[] => {
  return workerAvailabilities.flatMap((availability) => {
    const startDate = new Date(availability.from);
    const endDate = new Date(availability.to);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      console.warn('Invalid date in availability:', availability);
      return [];
    }

    const diffInDays = differenceInDays(endDate, startDate);

    if (diffInDays === 0) {
      return [availability];
    }

    return Array.from({ length: diffInDays + 1 }, (_, dayIndex) => {
      const isFirstDay = dayIndex === 0;
      const isLastDay = dayIndex === diffInDays;

      let newFromDate: Date;
      let newToDate: Date;

      if (availability.allDay) {
        newFromDate = startOfDay(addDays(startDate, dayIndex));
        newToDate = endOfDay(addDays(startDate, dayIndex));
      } else if (isFirstDay) {
        newFromDate = new Date(startDate);
        newToDate = endOfDay(startDate);
      } else if (isLastDay) {
        newFromDate = startOfDay(addDays(startDate, dayIndex));
        newToDate = new Date(endDate);
      } else {
        newFromDate = startOfDay(addDays(startDate, dayIndex));
        newToDate = endOfDay(addDays(startDate, dayIndex));
      }

      return {
        ...availability,
        allDay: availability.allDay,
        from: newFromDate.toISOString(),
        to: newToDate.toISOString(),
      };
    });
  });
};

/**
 * Converts worker availability data to FullCalendar event format
 */
const convertAvailabilitiesToCalendarEvents = (
  workerAvailabilities: IAvailabilityData[],
  companyId: number
) =>
  workerAvailabilities
    .sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime())
    .map((availability) => {
      const { jobAssignment: { job } = {} } = availability;
      const {
        jobAssignment: {
          job: { address, company } = {
            address: {},
            company: { id: null, name: '', profileImageUrl: '' },
          },
        } = {},
      } = availability;
      const { profileImageUrl } = company;

      const isUnavailable =
        availability.type === AvailabilityType.NOT_AVAILABLE;
      const isWorkDay = availability.type === AvailabilityType.WORK;

      const isSameCompany = companyId === company?.id;
      const bgColor = isSameCompany ? 'var(--light-gray)' : 'var(--lavender)';
      const classNames = isSameCompany ? ['same-company'] : ['other-company'];
      const avatar = isSameCompany ? profileImageUrl : defaultAvatar;

      const workerUnavailable = isUnavailable;

      const getEventTitle = () => {
        if (isUnavailable) {
          return 'Unavailable per worker';
        }
        if (!isSameCompany) {
          return availability.allDay
            ? 'Booked on TH, hours not specified'
            : 'Booked on TH';
        }
        return isWorkDay
          ? job?.position || AvailabilityTypeLabels[AvailabilityType.WORK]
          : AvailabilityTypeLabels[availability.type] || 'Event';
      };

      return {
        id: availability.id.toString(),
        title: getEventTitle(),
        start: new Date(availability.from),
        end: new Date(availability.to),
        allDay: availability.allDay,
        textColor: 'var(--black)',
        backgroundColor: workerUnavailable ? 'var(--light)' : bgColor,
        borderColor: workerUnavailable ? 'var(--transparent)' : bgColor,
        classNames: workerUnavailable ? ['not-available'] : classNames,
        extendedProps: {
          company,
          isSameCompany,
          avatar,
          job,
          address,
          workerUnavailable: isUnavailable,
        },
      };
    });

/**
 * Processes raw availability data for calendar display:
 * 1. Chunks multi-day events into daily events
 * 2. Converts to FullCalendar event format
 */
const processCalendarAvailabilities = (
  rawAvailabilities: IAvailabilityData[],
  companyId: number
) => {
  const chunked = rangeOfDateToChunkDays(rawAvailabilities);
  return convertAvailabilitiesToCalendarEvents(chunked, companyId);
};

/**
 * Safely coerce a Date, date-string, or nullish value into a Date.
 * Returns undefined when the input is missing or unparseable.
 */
const toDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? date : undefined;
};

// Date-only fields (experience start/end, degree date) are picked as local
// midnight by react-datepicker. Serializing via Date.toJSON() shifts to UTC,
// which lands on the previous day for users west of UTC. Anchor at UTC noon
// so every timezone within ±12h resolves to the same calendar day.
const toNoonUtc = (d: Date): Date =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0));

// Inverse of toNoonUtc: read UTC calendar parts so the edit-mode picker shows
// the same day that was selected, instead of drifting back a day when the ISO
// is reparsed in local time.
const fromNoonUtc = (
  value: Date | string | null | undefined
): Date | undefined => {
  const d = toDate(value);
  if (!d) return undefined;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

export {
  toDate,
  toNoonUtc,
  fromNoonUtc,
  UTC_TIMEZONE,
  parseToDate,
  dateDaysAgo,
  formatLocalDate,
  formatTime12h,
  isDateAfterOrNow,
  convertToTimezone,
  isSameDateOrAfter,
  isDateAfterOrToday,
  formatOpenDateRange,
  formatWorkDateRange,
  formatTimeOrDateNow,
  getDateNowByTimezone,
  getDifferenceInMinutes,
  getTodayDateByTimezone,
  calculateRemainingTime,
  createDateTimeFromDifferentDates,
  safeFormat,
  rangeOfDateToChunkDays,
  convertAvailabilitiesToCalendarEvents,
  processCalendarAvailabilities,
};
